/**
 * Debug Overlay Component for FirstDistro SDK
 * 
 * Full overlay showing SDK status, user/account context, and event count.
 * Opens when badge is clicked.
 */

import { getOverlayStyles } from './debug-styles';
import { 
  getStatusIcon, 
  getStatusText, 
  dismiss,
  type DebugStatus 
} from './debug-utils';

export interface DebugOverlayOptions {
  onClose?: () => void;
  onDismiss?: () => void;
}

export interface DebugOverlayState {
  status: DebugStatus;
  userContext: string;
  accountContext: string;
  eventCount: number;
}

/**
 * DebugOverlay class
 * Renders a full overlay with SDK status and context
 */
export class DebugOverlay {
  private container: HTMLElement | null = null;
  private shadowRoot: ShadowRoot | null = null;
  private overlay: HTMLElement | null = null;
  private options: DebugOverlayOptions;
  private state: DebugOverlayState;
  private isDragging: boolean = false;
  private dragStartX: number = 0;
  private dragStartY: number = 0;
  private overlayStartX: number = 0;
  private overlayStartY: number = 0;

  // Element references for updates
  private statusIndicator: HTMLElement | null = null;
  private statusValue: HTMLElement | null = null;
  private userValue: HTMLElement | null = null;
  private accountValue: HTMLElement | null = null;
  private eventCountValue: HTMLElement | null = null;

  constructor(options: DebugOverlayOptions = {}) {
    this.options = options;
    this.state = {
      status: 'loading',
      userContext: 'Anonymous',
      accountContext: 'Not set',
      eventCount: 0,
    };
  }

  /**
   * Render the overlay to the DOM (hidden by default)
   */
  render(): void {
    if (typeof window === 'undefined') return;
    if (this.container) return; // Already rendered

    // Create container element
    this.container = document.createElement('div');
    this.container.id = 'fd-debug-overlay-container';
    
    // Create shadow root for style isolation
    this.shadowRoot = this.container.attachShadow({ mode: 'closed' });
    
    // Add styles
    const styleElement = document.createElement('style');
    styleElement.textContent = getOverlayStyles();
    this.shadowRoot.appendChild(styleElement);
    
    // Create overlay element
    this.overlay = document.createElement('div');
    this.overlay.className = 'fd-overlay fd-hidden';
    this.overlay.innerHTML = `
      <div class="fd-header">
        <span class="fd-status-indicator">${getStatusIcon(this.state.status)}</span>
        <span class="fd-title">FirstDistro</span>
        <button class="fd-close" aria-label="Close">×</button>
      </div>
      <div class="fd-body">
        <div class="fd-section">
          <div class="fd-label">Status</div>
          <div class="fd-value fd-status">${getStatusIcon(this.state.status)} ${getStatusText(this.state.status)}</div>
        </div>
        <div class="fd-section">
          <div class="fd-label">User</div>
          <div class="fd-value fd-user">${this.escapeHtml(this.state.userContext)}</div>
        </div>
        <div class="fd-section">
          <div class="fd-label">Account</div>
          <div class="fd-value fd-account">${this.escapeHtml(this.state.accountContext)}</div>
        </div>
        <div class="fd-section">
          <div class="fd-label">Events (Session)</div>
          <div class="fd-value fd-event-count">${this.state.eventCount}</div>
        </div>
      </div>
    `;
    
    // Store references to dynamic elements
    this.statusIndicator = this.overlay.querySelector('.fd-status-indicator');
    this.statusValue = this.overlay.querySelector('.fd-status');
    this.userValue = this.overlay.querySelector('.fd-user');
    this.accountValue = this.overlay.querySelector('.fd-account');
    this.eventCountValue = this.overlay.querySelector('.fd-event-count');
    
    // Add event listeners
    const closeButton = this.overlay.querySelector('.fd-close');
    if (closeButton) {
      closeButton.addEventListener('click', this.handleClose.bind(this));
    }
    
    const header = this.overlay.querySelector('.fd-header');
    if (header) {
      header.addEventListener('mousedown', this.handleDragStart.bind(this));
    }
    
    // Add to shadow root
    this.shadowRoot.appendChild(this.overlay);
    
    // Add to document body
    document.body.appendChild(this.container);
    
    // Add global mouse event listeners for dragging
    document.addEventListener('mousemove', this.handleDragMove.bind(this));
    document.addEventListener('mouseup', this.handleDragEnd.bind(this));
  }

  /**
   * Escape HTML to prevent XSS
   */
  private escapeHtml(str: string): string {
    const div = document.createElement('div');
    div.textContent = str;
    return div.innerHTML;
  }

  /**
   * Handle close button click
   */
  private handleClose(): void {
    this.hide();
    if (this.options.onClose) {
      this.options.onClose();
    }
  }

  /**
   * Handle drag start
   */
  private handleDragStart(e: Event): void {
    const mouseEvent = e as MouseEvent;
    // Don't start drag if clicking the close button
    if ((mouseEvent.target as HTMLElement).classList.contains('fd-close')) {
      return;
    }
    
    this.isDragging = true;
    this.dragStartX = mouseEvent.clientX;
    this.dragStartY = mouseEvent.clientY;
    
    if (this.overlay) {
      const rect = this.overlay.getBoundingClientRect();
      this.overlayStartX = rect.left;
      this.overlayStartY = rect.top;
      
      // Remove default positioning during drag
      this.overlay.style.bottom = 'auto';
      this.overlay.style.right = 'auto';
      this.overlay.style.left = `${rect.left}px`;
      this.overlay.style.top = `${rect.top}px`;
    }
    
    mouseEvent.preventDefault();
  }

  /**
   * Handle drag move
   */
  private handleDragMove(e: MouseEvent): void {
    if (!this.isDragging || !this.overlay) return;
    
    const deltaX = e.clientX - this.dragStartX;
    const deltaY = e.clientY - this.dragStartY;
    
    const newX = this.overlayStartX + deltaX;
    const newY = this.overlayStartY + deltaY;
    
    // Keep overlay within viewport
    const maxX = window.innerWidth - this.overlay.offsetWidth;
    const maxY = window.innerHeight - this.overlay.offsetHeight;
    
    this.overlay.style.left = `${Math.max(0, Math.min(newX, maxX))}px`;
    this.overlay.style.top = `${Math.max(0, Math.min(newY, maxY))}px`;
  }

  /**
   * Handle drag end
   */
  private handleDragEnd(): void {
    this.isDragging = false;
  }

  /**
   * Show the overlay
   */
  show(): void {
    if (this.overlay) {
      this.overlay.classList.remove('fd-hidden');
    }
  }

  /**
   * Hide the overlay
   */
  hide(): void {
    if (this.overlay) {
      this.overlay.classList.add('fd-hidden');
    }
  }

  /**
   * Toggle overlay visibility
   */
  toggle(): void {
    if (this.isVisible()) {
      this.hide();
    } else {
      this.show();
    }
  }

  /**
   * Check if overlay is visible
   */
  isVisible(): boolean {
    return this.overlay ? !this.overlay.classList.contains('fd-hidden') : false;
  }

  /**
   * Update overlay state
   */
  update(state: Partial<DebugOverlayState>): void {
    this.state = { ...this.state, ...state };
    
    if (state.status !== undefined) {
      if (this.statusIndicator) {
        this.statusIndicator.textContent = getStatusIcon(state.status);
      }
      if (this.statusValue) {
        this.statusValue.textContent = `${getStatusIcon(state.status)} ${getStatusText(state.status)}`;
      }
    }
    
    if (state.userContext !== undefined && this.userValue) {
      this.userValue.textContent = state.userContext;
    }
    
    if (state.accountContext !== undefined && this.accountValue) {
      this.accountValue.textContent = state.accountContext;
    }
    
    if (state.eventCount !== undefined && this.eventCountValue) {
      this.eventCountValue.textContent = String(state.eventCount);
    }
  }

  /**
   * Update event count only
   */
  updateEventCount(count: number): void {
    this.state.eventCount = count;
    if (this.eventCountValue) {
      this.eventCountValue.textContent = String(count);
    }
  }

  /**
   * Dismiss overlay permanently
   */
  dismissPermanently(): void {
    dismiss();
    this.destroy();
    if (this.options.onDismiss) {
      this.options.onDismiss();
    }
  }

  /**
   * Check if overlay is rendered
   */
  isRendered(): boolean {
    return this.container !== null;
  }

  /**
   * Remove overlay from DOM
   */
  destroy(): void {
    // SSR-safe: check if document exists
    if (typeof document !== 'undefined') {
      // Remove global event listeners
      document.removeEventListener('mousemove', this.handleDragMove.bind(this));
      document.removeEventListener('mouseup', this.handleDragEnd.bind(this));
    }
    
    if (this.container && this.container.parentNode) {
      this.container.parentNode.removeChild(this.container);
    }
    
    this.container = null;
    this.shadowRoot = null;
    this.overlay = null;
    this.statusIndicator = null;
    this.statusValue = null;
    this.userValue = null;
    this.accountValue = null;
    this.eventCountValue = null;
  }
}
